import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import Menu from '@mui/material/Menu';
import authApi, { User, UserRole } from '../services/authApi';
import { useAuth } from '../contexts/AuthContext';

const ROLES: UserRole[] = ['admin', 'operator', 'viewer'];

function generatePassword(length = 16): string {
  const chars =
    'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*';
  const out: string[] = [];
  const array = new Uint32Array(length);
  crypto.getRandomValues(array);
  for (let i = 0; i < length; i++) out.push(chars[array[i] % chars.length]);
  return out.join('');
}

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

interface NewUserDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (email: string, password: string) => void;
}

const NewUserDialog: React.FC<NewUserDialogProps> = ({ open, onClose, onCreated }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState(() => generatePassword());
  const [role, setRole] = useState<UserRole>('viewer');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setEmail('');
      setPassword(generatePassword());
      setRole('viewer');
      setError(null);
    }
  }, [open]);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await authApi.createUser({ email: email.trim(), password, role });
      onCreated(email.trim(), password);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Errore creazione utente');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Nuovo utente</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <TextField
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoFocus
          required
          fullWidth
        />
        <TextField
          label="Password temporanea"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          helperText="Almeno 12 caratteri, con almeno una cifra o un simbolo"
          fullWidth
          InputProps={{
            endAdornment: (
              <Tooltip title="Rigenera">
                <IconButton onClick={() => setPassword(generatePassword())} size="small">
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ),
          }}
        />
        <TextField
          select
          label="Ruolo"
          value={role}
          onChange={(e) => setRole(e.target.value as UserRole)}
          fullWidth
        >
          {ROLES.map((r) => (
            <MenuItem key={r} value={r}>
              {r}
            </MenuItem>
          ))}
        </TextField>
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annulla</Button>
        <Button
          onClick={submit}
          variant="contained"
          disabled={submitting || !email || password.length < 12}
        >
          {submitting ? <CircularProgress size={20} /> : 'Crea'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface CredentialsDialogProps {
  open: boolean;
  email: string;
  password: string;
  onClose: () => void;
}

const CredentialsDialog: React.FC<CredentialsDialogProps> = ({
  open,
  email,
  password,
  onClose,
}) => {
  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      /* ignore */
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Utente creato</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Comunica queste credenziali fuori banda: non verranno piu' mostrate.
        </Alert>
        <Stack spacing={1}>
          <TextField
            label="Email"
            value={email}
            fullWidth
            InputProps={{
              readOnly: true,
              endAdornment: (
                <IconButton size="small" onClick={() => copy(email)}>
                  <CopyIcon fontSize="small" />
                </IconButton>
              ),
            }}
          />
          <TextField
            label="Password"
            value={password}
            fullWidth
            InputProps={{
              readOnly: true,
              endAdornment: (
                <IconButton size="small" onClick={() => copy(password)}>
                  <CopyIcon fontSize="small" />
                </IconButton>
              ),
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Ho copiato
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const UsersAdminPage: React.FC = () => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [credentials, setCredentials] = useState<{ email: string; password: string } | null>(
    null
  );
  const [menu, setMenu] = useState<{ anchor: HTMLElement; user: User } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await authApi.listUsers();
      setUsers(list);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Errore caricamento utenti');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const sorted = useMemo(
    () => [...users].sort((a, b) => a.email.localeCompare(b.email)),
    [users]
  );

  const openMenu = (u: User) => (e: React.MouseEvent<HTMLElement>) => {
    setMenu({ anchor: e.currentTarget, user: u });
  };

  const closeMenu = () => setMenu(null);

  const changeRole = async (role: UserRole) => {
    if (!menu) return;
    try {
      await authApi.updateUser(menu.user.id, { role });
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Errore modifica ruolo');
    } finally {
      closeMenu();
    }
  };

  const resetPassword = async () => {
    if (!menu) return;
    const newPassword = generatePassword();
    try {
      await authApi.updateUser(menu.user.id, { password: newPassword });
      setCredentials({ email: menu.user.email, password: newPassword });
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Errore reset password');
    } finally {
      closeMenu();
    }
  };

  const toggleActive = async () => {
    if (!menu) return;
    try {
      if (menu.user.is_active) {
        await authApi.deactivateUser(menu.user.id);
      } else {
        await authApi.updateUser(menu.user.id, { is_active: true });
      }
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Errore aggiornamento stato');
    } finally {
      closeMenu();
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Utenti</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNewOpen(true)}>
          Nuovo utente
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Ruolo</TableCell>
              <TableCell>Stato</TableCell>
              <TableCell>Ultimo login</TableCell>
              <TableCell align="right">Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
            {!loading && sorted.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Nessun utente
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              sorted.map((u) => (
                <TableRow key={u.id} hover>
                  <TableCell>
                    {u.email}
                    {me?.id === u.id && (
                      <Chip label="tu" size="small" sx={{ ml: 1 }} />
                    )}
                  </TableCell>
                  <TableCell sx={{ textTransform: 'capitalize' }}>{u.role}</TableCell>
                  <TableCell>
                    <Chip
                      label={u.is_active ? 'Attivo' : 'Disattivato'}
                      color={u.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(u.last_login)}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={openMenu(u)}>
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu anchorEl={menu?.anchor} open={!!menu} onClose={closeMenu}>
        {ROLES.filter((r) => r !== menu?.user.role).map((r) => (
          <MenuItem key={r} onClick={() => changeRole(r)}>
            Cambia ruolo in "{r}"
          </MenuItem>
        ))}
        <MenuItem onClick={resetPassword}>Reset password</MenuItem>
        <MenuItem onClick={toggleActive}>
          {menu?.user.is_active ? 'Disattiva' : 'Riattiva'}
        </MenuItem>
      </Menu>

      <NewUserDialog
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onCreated={(email, password) => {
          setCredentials({ email, password });
          load();
        }}
      />

      <CredentialsDialog
        open={!!credentials}
        email={credentials?.email || ''}
        password={credentials?.password || ''}
        onClose={() => setCredentials(null)}
      />
    </Box>
  );
};

export default UsersAdminPage;
